import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification,
  updateProfile,
  updatePassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { auth, functions, db, useEmulator } from "./firebase";
import { syncSubscriptionFromFirestore } from "./subscription";
import { createMfaChallenge, resolveTotpChallenge } from "./accountSecurity";
import { accountStorage } from "./accountStorage";
import { recordAuthFailure, signInErrorKind } from "./authDiagnostics";

const PENDING = "axoris.mock.pending";
const SESSION = "axoris.mock.session";
const CONSENT = "axoris.mock.consent";
const AUTH_NAME = "axoris.auth.name";
const VERIFY_COOLDOWN = "axoris.auth.verify_cooldown";

const norm = (e) => e?.trim().toLowerCase() ?? "";

async function completeSignIn(user, fallbackEmail = "") {
  if (localStorage.getItem("axoris.auth.uid") !== user.uid) {
    [CONSENT, SESSION, AUTH_NAME, "axoris.mock.subscription"].forEach((key) => localStorage.removeItem(key));
  }
  if (!user.emailVerified) {
    localStorage.setItem(PENDING, user.email || fallbackEmail);
    return { ok: true, verified: false };
  }
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (auth.currentUser?.uid !== user.uid) return { error: "credentials" };
    cacheConsent(user.uid, userDoc.exists() ? userDoc.data()?.consent : null);
  } catch { /* Use only this user's cache when offline. */ }
  if (auth.currentUser?.uid !== user.uid) return { error: "credentials" };
  localStorage.setItem(SESSION, user.email || fallbackEmail);
  localStorage.setItem("axoris.auth.uid", user.uid);
  if (user.displayName) localStorage.setItem(AUTH_NAME, user.displayName); else localStorage.removeItem(AUTH_NAME);
  localStorage.removeItem(PENDING);
  await syncSubscriptionFromFirestore(user.uid);
  if (auth.currentUser?.uid !== user.uid) return { error: "credentials" };
  return { ok: true, verified: true };
}

const signInFailure = (error) => error.code === "auth/multi-factor-auth-required"
  ? { mfa: createMfaChallenge(error) } : { error: signInErrorKind(error) };

async function finishSignIn(user, fallbackEmail = "") {
  const startedAt = Date.now();
  try { return await completeSignIn(user, fallbackEmail); }
  catch (error) {
    recordAuthFailure("session", error, startedAt);
    return { error: "unavailable" };
  }
}

export async function completeMfaLogin(challenge, uid, code) {
  const result = await resolveTotpChallenge(challenge, uid, code);
  return result.ok ? finishSignIn(result.credential.user) : result;
}

export const getPendingEmail = () => {
  return localStorage.getItem(PENDING) || auth.currentUser?.email || "";
};

export async function register({ name, email, password }) {
  const normalizedEmail = norm(email);
  let user;
  try {
    const cred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    user = cred.user;
  } catch (err) {
    return { error: err.code === "auth/email-already-in-use" ? "exists" : "registration" };
  }
  // The identity already exists even if profile setup or email delivery fails.
  localStorage.setItem(PENDING, normalizedEmail);
  localStorage.setItem("axoris.auth.uid", user.uid);
  try {
    if (name?.trim()) await updateProfile(user, { displayName: name.trim() });
  } catch {
    return { error: "setup", accountCreated: true };
  }
  try {
    await sendEmailVerification(user);
    localStorage.setItem(PENDING, normalizedEmail);
    localStorage.setItem(AUTH_NAME, name.trim());
    localStorage.setItem(VERIFY_COOLDOWN, String(Date.now()));
    return { ok: true };
  } catch {
    return { error: "verification", accountCreated: true };
  }
}

export async function login({ email, password }) {
  const normalizedEmail = norm(email);
  const startedAt = Date.now();
  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  } catch (error) {
    if (error.code !== "auth/multi-factor-auth-required") recordAuthFailure("email", error, startedAt);
    return signInFailure(error);
  }
  return finishSignIn(cred.user, normalizedEmail);
}

export async function googleSignIn() {
  const startedAt = Date.now();
  let cred;
  try {
    const provider = new GoogleAuthProvider();
    cred = await signInWithPopup(auth, provider);
  } catch (error) {
    if (error.code !== "auth/multi-factor-auth-required") recordAuthFailure("google", error, startedAt);
    return signInFailure(error);
  }
  return finishSignIn(cred.user);
}

export async function resendVerification() {
  if (!auth.currentUser) return { error: "unauthenticated" };
  if (auth.currentUser.emailVerified) return { error: "verified" };
  const lastSent = Number(localStorage.getItem(VERIFY_COOLDOWN) || 0);
  const now = Date.now();
  if (now - lastSent < 60000) {
    return { error: "cooldown", retryAfterSec: Math.ceil((60000 - (now - lastSent)) / 1000) };
  }

  try {
    await sendEmailVerification(auth.currentUser);
    localStorage.setItem(VERIFY_COOLDOWN, String(now));
    return { ok: true };
  } catch {
    return { error: "delivery" };
  }
}

export function markVerified() {
  if (useEmulator !== true) return false;
  const email = getPendingEmail();
  if (!email) return false;

  localStorage.setItem(SESSION, email);
  localStorage.removeItem(PENDING);

  // Jika di lingkungan dev/emulator, picu verifikasi server
  if (auth.currentUser) {
    try {
      const devVerify = httpsCallable(functions, "devVerifyEmail");
      devVerify().catch(() => {});
    } catch {}
  }
  return true;
}

export const getConsent = () => accountStorage().read(CONSENT, null);
export function cacheConsent(uid, consent) {
  const cache = accountStorage();
  if (cache.uid === uid) cache.write(CONSENT, consent ?? null);
}

export async function setConsent(consent, { fail = false } = {}) {
  const cache = accountStorage();
  if (fail || !consent || !consent.data) {
    return { error: "write" };
  }

  try {
    const setConsentFn = httpsCallable(functions, "setConsent");
    const res = await setConsentFn({
      consent: {
        data: Boolean(consent.data),
        ai: Boolean(consent.ai),
        analytics: Boolean(consent.analytics),
      },
    });

    if (res.data?.ok && res.data?.consent) {
      if (!cache.isCurrent()) return { error: "write" };
      cache.write(CONSENT, res.data.consent);
      await syncSubscriptionFromFirestore(cache.uid);
      return { ok: true };
    }
    return { error: "write" };
  } catch {
    return { error: "write" };
  }
}

export function getSessionUser() {
  const email = auth.currentUser?.email || localStorage.getItem(SESSION) || "";
  const name = auth.currentUser?.displayName || localStorage.getItem(AUTH_NAME) || "";
  return { name, email };
}

export async function changePassword(password) {
  if (!auth.currentUser) {
    return { error: "unauthenticated" };
  }
  try {
    await updatePassword(auth.currentUser, password);
    return { ok: true };
  } catch (err) {
    return { error: err.message, code: err.code };
  }
}

export async function sendPasswordReset(email) {
  try {
    await sendPasswordResetEmail(auth, norm(email));
  } catch {
    // Lupa sandi tidak mengungkap apakah email ada
  }
  return { ok: true };
}

export function deleteAccountData() {
  signOut(auth).catch(() => {});
  Object.keys(localStorage)
    .filter((k) => k.startsWith("axoris."))
    .forEach((k) => localStorage.removeItem(k));
}

// Keluar biasa: akhiri sesi Firebase, hapus kunci sesi saja.
// Preferensi perangkat (axoris.lang, axoris.theme) dipertahankan.
export async function signOutAccount() {
  try {
    await signOut(auth);
  } catch {}
  [PENDING, SESSION, CONSENT, AUTH_NAME, VERIFY_COOLDOWN, "axoris.auth.uid"].forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {}
  });
  return { ok: true };
}
